import { useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";

import { isSafari } from "../../../helpers/browser";
import theme, {
  DEFAULT_FONT_FAMILY,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
  TERTIARY_BLUE_COLOR,
  TERTIARY_GREEN_COLOR,
  TERTIARY_YELLOW_COLOR,
} from "../../../theme";
import { ReactECharts, ReactEChartsProps } from "./ReactECharts";

export type APDetailListItem = {
  label: string;
  value: string;
};

export type ChartAxisOptions = {
  type?: "category" | "value" | "time";
  formatter?: (value: string | number) => string;
  tooltipFormatter?: (value: string | number) => string;
};

type ChartDimensionLabels<T> = Record<keyof T, string>;

const buildEChartOption = <TDatum extends Record<string, number | string>>({
  title,
  data,
  dimensions,
  dimensionLabels,
  xAxisOptions = {},
  yAxisOptions = {},
  areaChart = false,
  stackChart = false,
  isMobile = false,
  customColors,
}: {
  title?: string;
  data: TDatum[];
  dimensions: (keyof TDatum)[];
  dimensionLabels: ChartDimensionLabels<TDatum>;
  xAxisOptions?: ChartAxisOptions;
  yAxisOptions?: ChartAxisOptions;
  areaChart?: boolean;
  stackChart?: boolean;
  isMobile?: boolean;
  customColors?: string[];
}): ReactEChartsProps["option"] => {
  const [xDimension, ...otherDimensions] = dimensions;
  const tooltipYFormatter =
    yAxisOptions.tooltipFormatter ?? yAxisOptions.formatter;

  // HACK: We need to add spaces to the legend to ensure the labels are aligned for Safari
  // 1. Find max length
  const maxLen = Math.max(
    ...Object.values(dimensionLabels).map((x) => x.length)
  );
  // 2. Create spaces string
  const spaces = "\u00A0".repeat(maxLen * 1.5);
  const _isSafari = isSafari();

  const option: ReactEChartsProps["option"] = {
    title: title
      ? {
          text: title,
          left: "center",
          top: "left",
          textStyle: {
            fontWeight: 500,
            fontSize: "1rem",
          },
        }
      : undefined,
    animation: true,
    animationEasing: "backIn",
    textStyle: {
      fontFamily: DEFAULT_FONT_FAMILY,
      fontWeight: 500,
    },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value) =>
        tooltipYFormatter
          ? tooltipYFormatter(value as string | number)
          : (value as string),
      formatter: (params) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const xLabel = (params as any)[0].axisValueLabel;
        const lines = [
          `<strong>${
            xAxisOptions.formatter
              ? xAxisOptions.formatter(xLabel as string | number)
              : xLabel
          }</strong>`,
        ];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as any).forEach((item: any) => {
          const label = item.seriesName;
          const value = Object.values(item.data)[item.seriesIndex + 1];

          lines.push(
            `${item.marker}${label}: ${
              tooltipYFormatter
                ? tooltipYFormatter(value as string | number)
                : value
            }`
          );
        });

        return lines.join("<br />");
      },
    },
    legend:
      otherDimensions.length > 1
        ? {
            icon: "circle",
            itemWidth: 10,
            itemGap: 15,
            textStyle: {
              fontSize: "0.688rem",
            },
            formatter: (name) => {
              return `${name}${_isSafari ? spaces : ""}`;
            },
          }
        : undefined,
    grid: {
      top: title ? 36 : 0,
      left: _isSafari ? 48 : 0,
      right: _isSafari ? 36 : 0,
      bottom:
        otherDimensions.length > 1
          ? isMobile
            ? 110
            : 80
          : _isSafari && isMobile
          ? 48
          : 24,
    },
    xAxis: {
      type: (xAxisOptions.type ?? "category") as "category" | undefined,
      axisLabel: {
        formatter: xAxisOptions.formatter ?? undefined,
        showMaxLabel: data.length <= 2,
        fontSize: "0.688rem",
        rotate: isMobile ? 315 : undefined,
      },
      boundaryGap: false,
      ...(xAxisOptions.type === "time" && {
        // 1. Force a strict structural minimum gap (e.g. at least 1 apart)
        minInterval: 1000 * 60 * 60 * 24 * 28,

        // 2. Add an explicit maxInterval ceiling constraint so the engine behaves
        // predictably across varying data date ranges (e.g. 1-5 years of data)
        maxInterval: 1000 * 60 * 60 * 24 * 365,

        // 3. Keep splitNumber low to continuously nudge ECharts toward fewer labels
        splitNumber: 5,
      }),
    },
    yAxis: {
      type: "value",
      axisLabel: {
        formatter: yAxisOptions.formatter ?? undefined,
        showMinLabel: false,
        fontSize: "0.688rem",
      },
      show: true,
      minInterval: 1,
    },
    color: customColors ?? [
      PRIMARY_COLOR,
      SECONDARY_COLOR,
      TERTIARY_YELLOW_COLOR,
      TERTIARY_GREEN_COLOR,
      TERTIARY_BLUE_COLOR,
    ],
    animationDuration: 250,
    dataset: {
      source: data,
    },
    series: otherDimensions.map((dimension) => ({
      type: "line",
      name: dimensionLabels[dimension],
      symbol: "none",
      stack: stackChart ? "Total" : undefined,
      areaStyle: areaChart ? {} : undefined,
      emphasis: {
        focus: "series",
        blurScope: "coordinateSystem",
      },
      smooth: 0.3,
      encode:
        xAxisOptions.type === "time"
          ? {
              x: xDimension as string,
              y: dimension as string,
            }
          : undefined,
    })),
  };

  return option;
};

const buildEmptySource = <
  TDatum extends Record<string, number | string | Date>
>({
  data,
  dimensions,
}: {
  data: TDatum[];
  dimensions: (keyof TDatum)[];
}): TDatum[] => {
  const [, ...otherDimensions] = dimensions;

  return data.map((item) => {
    const newItem = { ...item }; // clone first

    otherDimensions.forEach((dimension) => {
      if (typeof newItem[dimension] === "number") {
        newItem[dimension] = 0 as TDatum[keyof TDatum];
      }
    });

    return newItem;
  });
};

const ReactELineChart = <TDatum extends Record<string, number | string>>({
  title,
  data,
  dimensions,
  dimensionLabels,
  xAxisOptions,
  yAxisOptions,
  areaChart,
  stackChart,
  customColors,
}: {
  title?: string;
  data: TDatum[];
  dimensions: (keyof TDatum)[];
  dimensionLabels: ChartDimensionLabels<TDatum>;
  xAxisOptions?: ChartAxisOptions;
  yAxisOptions?: ChartAxisOptions;
  areaChart?: boolean;
  stackChart?: boolean;
  customColors?: string[];
}) => {
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down("sm"));

  const option = buildEChartOption({
    title,
    data,
    dimensions,
    dimensionLabels,
    xAxisOptions,
    yAxisOptions,
    areaChart,
    stackChart,
    isMobile: mobileBreakpoint,
    customColors,
  });
  const emptySource = buildEmptySource({ data, dimensions });

  const [eChartData, setEChartData] = useState({
    ...option,
    animation: false,
    yAxis: {
      ...option.yAxis,
      show: false,
    },
    dataset: {
      ...option.dataset,
      source: emptySource,
    },
  } as ReactEChartsProps["option"]);

  useEffect(() => {
    setTimeout(() => setEChartData(option), 0);
  }, []);

  return <ReactECharts option={eChartData} />;
};

export default ReactELineChart;
